import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { IItemOperation, INodeColor, IOrderPosition, ITerminalStatus, TerminalStatusState } from '../models/production-plan.models';

export interface IProductionPlanInfoDialogueColumn {
	title: string;
	value: string | number;
}

export interface IProductionPlanInfoDialogueData {
	item: IOrderPosition;
	batchName: string;
	infoColumns: IProductionPlanInfoDialogueColumn[];
	terminalStatuses?: ITerminalStatus[];
	terminalNames?: Map<string, string>;
}

@Component({
	selector: 'app-production-plan-info-dialogue',
	templateUrl: './production-plan-info-dialogue.component.html',
	styleUrl: './production-plan-info-dialogue.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ProductionPlanInfoDialogueComponent {
	isFullscreen = false;
	private nodeColors?: Map<string, INodeColor>;

	constructor(
		@Inject(MAT_DIALOG_DATA) public data: IProductionPlanInfoDialogueData,
		private dialogRef: MatDialogRef<ProductionPlanInfoDialogueComponent>,
	) {}

	get hasTerminalStatuses(): boolean {
		return (this.data.terminalStatuses?.length ?? 0) > 0;
	}

	get terminalStatuses(): ITerminalStatus[] {
		return (this.data.terminalStatuses ?? [])
			.map((terminalStatus) => ({
				...terminalStatus,
				itemOperations: [...terminalStatus.itemOperations].sort((a, b) => (a.alias ?? '').localeCompare(b.alias ?? '')),
			}))
			.sort((a, b) => this.getTerminalName(a).localeCompare(this.getTerminalName(b)));
	}

	get nodeColorById(): Map<string, INodeColor> {
		if (!this.nodeColors) {
			this.nodeColors = new Map();
			for (const terminalStatus of this.data.terminalStatuses ?? []) {
				for (const operation of terminalStatus.itemOperations) {
					if (!operation.id) continue;
					const { status } = this.getOperationProgress(operation);
					this.nodeColors.set(operation.id, {
						background: this.getStatusTrackColor(status),
						accent: this.getStatusBarColor(status),
					});
				}
			}
		}
		return this.nodeColors;
	}

	getOperationProgress(operation: IItemOperation): {
		percent: number;
		status: TerminalStatusState;
		text: string;
	} {
		const total = operation.items.length;
		const done = operation.items.filter((item) => item.completed).length;
		const ready = operation.items.filter((item) => item.ready).length;
		const status = this.getOperationStatus(total, done, ready);

		return {
			percent: this.getProgressPercent(status, total, done, ready),
			status,
			text: this.getProgressText(status, total, done, ready),
		};
	}

	private getOperationStatus(total: number, done: number, ready: number): TerminalStatusState {
		if (total > 0 && done === total) return TerminalStatusState.Completed;
		if (done > 0) return TerminalStatusState.InProgress;
		if (ready > 0) return TerminalStatusState.Ready;
		return TerminalStatusState.Pending;
	}

	private getProgressPercent(status: TerminalStatusState, total: number, done: number, ready: number): number {
		if (total === 0) return 0;
		const progress = this.getProgressValue(status, total, done, ready);
		return Math.round((progress / total) * 100);
	}

	private getProgressText(status: TerminalStatusState, total: number, done: number, ready: number): string {
		return `${this.getProgressValue(status, total, done, ready)}/${total}`;
	}

	private getProgressValue(status: TerminalStatusState, total: number, done: number, ready: number): number {
		if (status === TerminalStatusState.Ready) return ready;
		if (status === TerminalStatusState.Pending) return total;
		return done;
	}

	getTerminalName(terminalStatus: ITerminalStatus): string {
		return this.data.terminalNames?.get(terminalStatus.terminalId) ?? '';
	}

	getStatusTrackColor(status: TerminalStatusState): string {
		if (status === TerminalStatusState.Pending) return '#ffcdd2';
		if (status === TerminalStatusState.Ready) return '#bbdefb';
		if (status === TerminalStatusState.InProgress) return '#fff9c4';
		if (status === TerminalStatusState.Completed) return '#c8e6c9';
		return '#f7f7f7';
	}

	getStatusBarColor(status: TerminalStatusState): string {
		if (status === TerminalStatusState.Pending) return '#ffcdd2';
		if (status === TerminalStatusState.Ready) return '#1e88e5';
		if (status === TerminalStatusState.InProgress) return '#fdd835';
		if (status === TerminalStatusState.Completed) return '#43a047';
		return '#d7dce3';
	}

	onTabChange(index: number): void {
		if (index !== 1) {
			this.isFullscreen = false;
		}
		this.dialogRef.updateSize(index === 1 ? '1520px' : '760px', '85vh');
	}

	onFullscreenToggle(): void {
		this.isFullscreen = !this.isFullscreen;
		this.dialogRef.updateSize(this.isFullscreen ? '99vw' : '1520px', this.isFullscreen ? '99vh' : '85vh');
	}

	close(): void {
		this.dialogRef.close();
	}
}
